
import { Guid } from 'guid-typescript';

import { useMutation } from '@apollo/client';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import {
  CreateContractLines,
  CreateContractLinesVariables,
} from '@/graphql/contracts/_generated_/createContractLines.mutation';
import CreateContractLinesMutation from '@/graphql/contracts/createContractLines.mutation.gql';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import { ContractLineForBulkAddInputType, ContractOriginEnum } from '@/graphql/contracts/_generated_/contracts';
import { mapFieldsToCreateContractLine, mapFieldsToCreateVariableContractLine } from './mapper';
import { notificationService } from '@/services/notification/notification.client';
import { VARIABLE_TYPE } from '@/config/statuses.config';

export type ContractLineToCreate = {
  id: UUID,
  billingInterval: ContractLine['billingInterval'],
  billingStartDate: ContractLineForBulkAddInputType['billingStartDate'],
  unitPriceFrequency: ContractLine['unitPriceFrequency'],
  billingType: ContractLine['billingType'],
  comments: ContractLine['comments'],
  quantity: ContractLine['quantity'],
  stockCode: ContractLine['product'],
  unitPrice: ContractLine['unitPrice'],
  lineStartDate: ContractLine['lineStartDate'],
  shipToCustomer: ContractLine['shipToCustomer'],
  invoiceToCustomer: ContractLine['invoiceToCustomer'],
  unitType: ContractLine['unitType'],
  poNumber: ContractLine['poNumber'],
  proRataEndDate: string | null | undefined,
};

export type VariableContractLineToCreate = {
  id: UUID,
  billingInterval: ContractLine['billingInterval'],
  billingStartDate: ContractLineForBulkAddInputType['billingStartDate'],
  billingType: ContractLine['billingType'],
  comments: ContractLine['comments'],
  stockCode: ContractLine['product'],
  unitPrice: ContractLine['unitPrice'],
  lineStartDate: ContractLine['lineStartDate']
  shipToCustomer: ContractLine['shipToCustomer'],
  invoiceToCustomer: ContractLine['invoiceToCustomer'],
  unitType: ContractLine['unitType'],
  poNumber: ContractLine['poNumber']
};

type CreateContractLineResult = {
  success: true;
  data: CreateContractLines['createContractLines'];
} | {
  success: false;
  error: string;
};

export function getEmptyContractLineData(contract?: Contract): Partial<ContractLineToCreate> {
  const id = Guid.create().toString();

  return {
    id,
    billingInterval: undefined,
    billingStartDate: undefined,
    unitPriceFrequency: undefined,
    billingType: undefined,
    comments: undefined,
    quantity: undefined,
    stockCode: undefined,
    unitPrice: undefined,
    lineStartDate: undefined,
    shipToCustomer: contract?.shipToCustomer ?? undefined,
    invoiceToCustomer: contract?.invoiceToCustomer ?? undefined,
    unitType: undefined,
    poNumber: undefined,
    proRataEndDate: undefined
  };
}

export function getEmptyVariableContractLineData(contract?: Contract): Partial<VariableContractLineToCreate> {
  const id = Guid.create().toString();

  return {
    id,
    billingInterval: undefined,
    billingStartDate: undefined,
    billingType: undefined,
    comments: undefined,
    stockCode: undefined,
    unitPrice: undefined,
    lineStartDate: undefined,
    shipToCustomer: contract?.shipToCustomer ?? undefined,
    invoiceToCustomer: contract?.invoiceToCustomer ?? undefined,
    unitType: undefined,
    poNumber: undefined
  };
}

export default function useCreateContractLines(contractId: UUID, contractOrigin?: ContractOriginEnum | null) {
  const [_createContractLines] = useMutation<CreateContractLines, CreateContractLinesVariables>(
    CreateContractLinesMutation,
    {
      client: apolloClientContracts,
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetContractDetailsQuery,
          variables: {
            contractId,
          }
        }
      ]
    },
  );

  const createContractLines = async (contractLines: ContractLineToCreate[] | VariableContractLineToCreate[]):
  Promise<CreateContractLineResult> => {
    const result = await _createContractLines({
      variables: {
        contractID: contractId,
        contractLines: contractLines[0].billingType.formattedType === VARIABLE_TYPE ?
          mapFieldsToCreateVariableContractLine(contractLines, contractOrigin) :
          mapFieldsToCreateContractLine(contractLines as ContractLineToCreate[], contractOrigin)
      }
    });

    if (!result.data?.createContractLines) {
      throw new Error('Unknown error occured during creating contract line');
    }

    return {
      success: true as const,
      data: result.data.createContractLines
    };
  };

  return {
    createContractLines,
  } as const;
}
